import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  Check, 
  Star, 
  Zap, 
  Crown, 
  AlertTriangle, 
  ArrowRight,
  Users,
  MessageSquare,
  Calendar,
  BarChart3,
  Search,
  FileText,
  Award,
  Sparkles,
  Loader2,
  Timer
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface PlanData {
  id: string;
  tier: string;
  name: string;
  monthly_price_cents: number;
  annual_price_cents: number;
  contact_limit: number | null;
  features: string[];
}

interface FoundingAgentSession {
  applicationId: string;
  username: string;
  fullName: string;
  email: string;
  positionNumber: number;
  isFoundingAgent: boolean;
  approvalExpiresAt?: string;
}

const AgentPricing = () => {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(true);
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [foundingCount, setFoundingCount] = useState<number>(0);
  const [foundingAgentSession, setFoundingAgentSession] = useState<FoundingAgentSession | null>(null);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);

  useEffect(() => {
    // Check for founding agent session
    const storedSession = sessionStorage.getItem('foundingAgentSession');
    if (storedSession) {
      try {
        const session = JSON.parse(storedSession);
        if (session.isFoundingAgent) {
          setFoundingAgentSession(session);
        }
      } catch (e) {
        console.error('Error parsing founding agent session:', e);
      }
    }
    
    const fetchData = async () => {
      const [plansResult, countResult] = await Promise.all([
        supabase.from('agent_subscription_plans').select('*').eq('is_active', true).order('monthly_price_cents'),
        supabase.rpc('get_founding_agent_count')
      ]);

      if (plansResult.data) {
        setPlans(plansResult.data.map(p => ({
          ...p,
          features: Array.isArray(p.features) ? p.features : JSON.parse(p.features as string)
        })));
      }
      if (countResult.data !== null) {
        setFoundingCount(countResult.data);
      }
    };
    fetchData();
  }, []);

  const remainingSpots = Math.max(0, 200 - foundingCount);

  const getTimeRemaining = (expiresAt: string | undefined) => {
    if (!expiresAt) return null;
    
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();
    
    if (diff <= 0) return { expired: true, text: 'Expired' };
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return { expired: false, text: `${days}d ${hours}h remaining` };
    }
    return { expired: false, text: `${hours}h remaining` };
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'essential': return <Star className="h-6 w-6" />;
      case 'professional': return <Zap className="h-6 w-6" />;
      case 'premium': return <Crown className="h-6 w-6" />;
      default: return <Star className="h-6 w-6" />;
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'essential': return 'border-blue-500/30 bg-blue-500/5';
      case 'professional': return 'border-purple-500/30 bg-purple-500/5';
      case 'premium': return 'border-yellow-500/30 bg-yellow-500/5';
      default: return '';
    }
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(0)}`;
  };

  // Calculate founding agent pricing display (24 months for price of 12 = 50% off effective monthly)
  const getFoundingAgentPrice = (plan: PlanData) => {
    // They pay the annual price but get 24 months
    return plan.annual_price_cents;
  };

  const handleSelectPlan = async (plan: PlanData) => {
    setIsProcessing(plan.id);
    
    try {
      const isFoundingAgent = !!foundingAgentSession;
      
      // For founding agents, force annual billing
      const billingCycle = isFoundingAgent ? 'annual' : (isAnnual ? 'annual' : 'monthly');
      
      // Call the agent-subscription edge function to create checkout
      const { data, error } = await supabase.functions.invoke('agent-subscription', {
        body: {
          action: 'create_checkout',
          planId: plan.id,
          billingCycle,
          userId: foundingAgentSession?.applicationId || null, // For internal users
          userType: isFoundingAgent ? 'internal' : 'supabase',
          isFoundingAgent,
          applicationId: foundingAgentSession?.applicationId || null
        }
      });

      if (error) throw error;
      
      if (!data?.success) {
        if (data?.message?.includes('STRIPE_SECRET_KEY')) {
          toast.error('Payment system is being configured. Please try again later.');
        } else {
          throw new Error(data?.error || 'Failed to create checkout session');
        }
        return;
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Failed to start checkout. Please try again.');
    } finally {
      setIsProcessing(null);
    }
  };

  const timeRemaining = foundingAgentSession ? getTimeRemaining(foundingAgentSession.approvalExpiresAt) : null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 pt-28 pb-12">
        {/* Founding Agent Welcome Banner */}
        {foundingAgentSession && (
          <Alert className="mb-8 border-2 border-primary/50 bg-primary/10 max-w-4xl mx-auto">
            <Sparkles className="h-5 w-5 text-primary" />
            <AlertTitle className="font-bold text-primary">
              Welcome, Founding Agent #{foundingAgentSession.positionNumber}!
            </AlertTitle>
            <AlertDescription className="text-primary/90">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span>
                  As an approved Founding Agent, you're eligible for exclusive BETA pricing below. 
                  <strong> Pay for 12 months and get 24 months of access!</strong>
                </span>
                {timeRemaining && !timeRemaining.expired && (
                  <Badge variant="outline" className="border-yellow-500 text-yellow-600 flex items-center gap-1 w-fit">
                    <Timer className="h-3 w-3" />
                    {timeRemaining.text}
                  </Badge>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Expiration Warning */}
        {timeRemaining?.expired && (
          <Alert className="mb-8 border-2 border-red-500/50 bg-red-500/10 max-w-4xl mx-auto">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <AlertTitle className="font-bold text-red-700">
              Approval Expired
            </AlertTitle>
            <AlertDescription className="text-red-700">
              Your 7-day payment window has passed. Please contact support or reapply for Founding Agent status.
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <section className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">
            {foundingAgentSession ? 'Founding Agent BETA Pricing' : 'Agent / NIL Tools Subscriber Pricing'}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Professional tools designed for discovery, outreach, and NIL-related workflows—without acting as representation.
          </p>
        </section>

        {/* Critical Disclaimer */}
        <Alert className="mb-8 border-2 border-yellow-500/50 bg-yellow-500/10 max-w-4xl mx-auto">
          <AlertTriangle className="h-5 w-5 text-yellow-600" />
          <AlertTitle className="font-bold text-yellow-700">
            IMPORTANT: Platform Role Clarification
          </AlertTitle>
          <AlertDescription className="text-yellow-700">
            <strong>Players Lockeroom provides tools; it is not acting as representation or agent.</strong>
          </AlertDescription>
        </Alert>

        {/* Billing Toggle - only show if not founding agent OR show locked to annual */}
        <div className="flex items-center justify-center gap-4 mb-12">
          {foundingAgentSession ? (
            <div className="text-center">
              <Badge className="bg-primary/20 text-primary border-primary/30 text-lg px-4 py-2">
                <Sparkles className="h-4 w-4 mr-2" />
                Annual Only - 24 Months for the Price of 12
              </Badge>
              <p className="text-sm text-muted-foreground mt-2">Founding Agent exclusive: Lifetime price lock included</p>
            </div>
          ) : (
            <>
              <Label htmlFor="billing-toggle" className={!isAnnual ? 'font-semibold' : 'text-muted-foreground'}>
                Monthly
              </Label>
              <Switch
                id="billing-toggle"
                checked={isAnnual}
                onCheckedChange={setIsAnnual}
              />
              <Label htmlFor="billing-toggle" className={isAnnual ? 'font-semibold' : 'text-muted-foreground'}>
                Annual
                <Badge variant="secondary" className="ml-2 bg-green-500/10 text-green-600">
                  Save ~17%
                </Badge>
              </Label>
            </>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
          {plans.map((plan) => (
            <Card 
              key={plan.id} 
              className={`relative border-2 ${getTierColor(plan.tier)} ${plan.tier === 'professional' ? 'scale-105 shadow-lg' : ''}`}
            >
              {plan.tier === 'professional' && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500">
                  Most Popular
                </Badge>
              )}
              
              <CardHeader className="text-center pb-2">
                <div className={`w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center ${
                  plan.tier === 'essential' ? 'bg-blue-500/20 text-blue-500' :
                  plan.tier === 'professional' ? 'bg-purple-500/20 text-purple-500' :
                  'bg-yellow-500/20 text-yellow-500'
                }`}>
                  {getTierIcon(plan.tier)}
                </div>
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <CardDescription>
                  {plan.tier === 'essential' && 'For new or smaller operators evaluating NIL tools'}
                  {plan.tier === 'professional' && 'For active agents managing consistent outreach'}
                  {plan.tier === 'premium' && 'For high-volume professionals and agencies'}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="text-center">
                <div className="mb-6">
                  {foundingAgentSession ? (
                    // Founding Agent Pricing Display
                    <>
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="text-lg text-muted-foreground line-through">
                          {formatPrice(plan.annual_price_cents * 2 / 12)}/mo
                        </span>
                        <Badge className="bg-primary/20 text-primary border-0">BETA</Badge>
                      </div>
                      <span className="text-4xl font-bold text-primary">
                        {formatPrice(plan.annual_price_cents / 24)}
                      </span>
                      <span className="text-muted-foreground">/month</span>
                      <p className="text-sm text-primary mt-1">
                        {formatPrice(plan.annual_price_cents)} for 24 months
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        (Pay for 12, get 24!)
                      </p>
                    </>
                  ) : (
                    // Regular Pricing Display
                    <>
                      <span className="text-4xl font-bold">
                        {formatPrice(isAnnual ? plan.annual_price_cents / 12 : plan.monthly_price_cents)}
                      </span>
                      <span className="text-muted-foreground">/month</span>
                      {isAnnual && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {formatPrice(plan.annual_price_cents)} billed annually
                        </p>
                      )}
                    </>
                  )}
                </div>

                <div className="text-sm font-medium mb-4 p-2 bg-muted rounded-lg">
                  {plan.contact_limit ? (
                    <span className="flex items-center justify-center gap-2">
                      <Users className="h-4 w-4" />
                      Up to {plan.contact_limit} contacts/month
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2 text-yellow-600">
                      <Users className="h-4 w-4" />
                      Unlimited contacts
                    </span>
                  )}
                </div>

                <Separator className="my-4" />

                <ul className="space-y-3 text-left text-sm">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              
              <CardFooter>
                <Button 
                  className="w-full" 
                  variant={plan.tier === 'professional' ? 'default' : 'outline'}
                  onClick={() => handleSelectPlan(plan)}
                  disabled={isProcessing !== null || (timeRemaining?.expired)}
                >
                  {isProcessing === plan.id ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      {foundingAgentSession ? 'Complete Payment' : 'Get Started'}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Founding Agent Beta CTA - only show if NOT already a founding agent */}
        {remainingSpots > 0 && !foundingAgentSession && (
          <section className="mb-16">
            <Card className="border-2 border-primary/30 bg-gradient-to-r from-primary/5 to-primary/10 max-w-4xl mx-auto">
              <CardContent className="py-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 rounded-2xl bg-primary/20 flex items-center justify-center">
                      <Award className="h-10 w-10 text-primary" />
                    </div>
                  </div>
                  <div className="flex-grow text-center md:text-left">
                    <Badge className="mb-2 bg-primary/20 text-primary border-primary/30">
                      Limited Offer • {remainingSpots} spots remaining
                    </Badge>
                    <h3 className="text-2xl font-bold mb-2">Founding Agent Beta Access</h3>
                    <p className="text-muted-foreground mb-4">
                      The first 200 approved Agent / NIL Tools subscribers who choose an annual plan receive:
                    </p>
                    <ul className="grid md:grid-cols-2 gap-2 text-sm mb-4">
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>24 months for the price of 12</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Lifetime price lock</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Founding Agent badge</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary" />
                        <span>Priority feature access</span>
                      </li>
                    </ul>
                  </div>
                  <div className="flex-shrink-0">
                    <Button size="lg" onClick={() => navigate('/agent-beta')}>
                      Apply Now
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Feature Comparison */}
        <section className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-center mb-8">Feature Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-4 px-4">Feature</th>
                  <th className="text-center py-4 px-4">Essential</th>
                  <th className="text-center py-4 px-4 bg-purple-500/5">Professional</th>
                  <th className="text-center py-4 px-4">Premium</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    Directory Placement
                  </td>
                  <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4 bg-purple-500/5"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    Athlete Contacts/Month
                  </td>
                  <td className="text-center py-3 px-4">200</td>
                  <td className="text-center py-3 px-4 bg-purple-500/5">500</td>
                  <td className="text-center py-3 px-4 font-semibold text-yellow-600">Unlimited</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    Advanced Filters
                  </td>
                  <td className="text-center py-3 px-4 text-muted-foreground">Basic</td>
                  <td className="text-center py-3 px-4 bg-purple-500/5"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    Messaging Templates
                  </td>
                  <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4 bg-purple-500/5"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    Calendar Scheduling
                  </td>
                  <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4 bg-purple-500/5"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    Deal Workflow Templates
                  </td>
                  <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                  <td className="text-center py-3 px-4 bg-purple-500/5"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <Star className="h-4 w-4 text-muted-foreground" />
                    Priority Placement
                  </td>
                  <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                  <td className="text-center py-3 px-4 bg-purple-500/5"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                    Full Analytics Dashboard
                  </td>
                  <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                  <td className="text-center py-3 px-4 bg-purple-500/5 text-muted-foreground">—</td>
                  <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 flex items-center gap-2">
                    <Award className="h-4 w-4 text-muted-foreground" />
                    Co-Branded Profile
                  </td>
                  <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                  <td className="text-center py-3 px-4 bg-purple-500/5 text-muted-foreground">—</td>
                  <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Terms */}
        <section className="max-w-2xl mx-auto text-center text-sm text-muted-foreground">
          <p className="mb-2">
            Founding Agent Beta Access is limited and subject to approval. Payment must be completed within 7 days of approval.
          </p>
          <Button variant="link" onClick={() => navigate('/agent/terms')}>
            View Agent / NIL Tools Subscriber Terms
          </Button>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AgentPricing;